import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Mail, Instagram, MapPin, CheckCircle2 } from "lucide-react";
import { SITE, BRAND_PHOTOS } from "@/config/site";
import { SectionLabel, SectionHeading } from "./SectionLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Content Creation",
  "Social Media Management",
  "Brand Identity",
  "Website & Digital Presence",
  "Creative Strategy",
  "Sports & Team Media",
  "Not sure — open to recommendations",
];

const BUDGETS = [
  "Under $1,000",
  "$1,000 – $2,500",
  "$2,500 – $5,000",
  "$5,000 – $10,000",
  "$10,000+",
  "Not sure yet",
];

const TIMELINES = [
  "ASAP",
  "Within 1 month",
  "1 – 3 months",
  "3 – 6 months",
  "Flexible",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business: "",
    service: "",
    budget: "",
    timeline: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setErrorMsg("Please fill in your name, email, and project details.");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        business: form.business.trim() || null,
        service: form.service || null,
        budget: form.budget || null,
        timeline: form.timeline || null,
        message: form.message.trim(),
        website: form.website, // honeypot
      });
      setStatus("success");
      setForm({
        name: "",
        email: "",
        business: "",
        service: "",
        budget: "",
        timeline: "",
        message: "",
        website: "",
      });
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again, or email directly."
      );
    }
  };

  // Field & label styling — luxury dark form
  const fieldBase =
    "w-full bg-[#0a0907] border border-[rgba(198,162,99,0.25)] rounded-xl px-5 py-3.5 text-[15px] text-[rgb(var(--bone))] placeholder:text-[rgb(var(--bone-dim))]/55 focus:outline-none focus:border-[rgb(var(--gold))] focus:ring-1 focus:ring-[rgb(var(--gold))]/30 transition-all";
  const labelCls =
    "block font-cinzel text-[11px] tracking-[0.32em] uppercase text-[rgb(var(--bone-dim))] mb-2.5";
  const selectCls = `${fieldBase} appearance-none pr-10 bg-no-repeat`;
  const selectStyle = {
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ddc58e' d='M6 8.5L1.5 4h9z'/%3E%3C/svg%3E\")",
    backgroundPosition: "right 1.1rem center",
    backgroundSize: "12px",
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 border-t border-[rgba(198,162,99,0.12)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        {/* Left — heading + portrait + details */}
        <div className="lg:col-span-5">
          <SectionLabel number="06" label="Contact" />
          <SectionHeading className="mt-6">
            Ready to elevate your{" "}
            <span className="italic text-gold-metal">brand</span>?
          </SectionHeading>
          <p className="mt-7 text-[rgb(var(--bone-dim))] leading-relaxed max-w-md">
            Let&apos;s build a stronger digital presence for your business. Send a
            brief and Uberti Creative will follow up with next steps.
          </p>

          <div className="mt-8 hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden border border-[rgba(198,162,99,0.18)] aspect-[4/5] max-w-[340px]">
              <img
                src={BRAND_PHOTOS.brandMockup}
                alt="Uberti Creative brand identity mockup"
                className="absolute inset-0 w-full h-full object-contain bg-[#0a0907]"
                style={{ objectPosition: "50% 50%" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,0.78)] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-[11px] tracking-[0.3em] uppercase text-[rgb(var(--gold-soft))]">
                  Brand Identity
                </div>
                <div className="mt-1 font-serif-display italic text-lg text-[rgb(var(--bone))]">
                  Uberti Creative
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 space-y-5">
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email"
              className="group flex items-center gap-3 text-[rgb(var(--bone))] hover:text-[rgb(var(--gold-soft))] transition-colors"
            >
              <Mail size={18} className="text-[rgb(var(--gold-soft))]" />
              {SITE.email}
              <ArrowUpRight
                size={14}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </a>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram"
              className="group flex items-center gap-3 text-[rgb(var(--bone))] hover:text-[rgb(var(--gold-soft))] transition-colors"
            >
              <Instagram size={18} className="text-[rgb(var(--gold-soft))]" />
              @{SITE.instagram}
              <ArrowUpRight
                size={14}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </a>
            <div className="flex items-center gap-3 text-[rgb(var(--bone))]">
              <MapPin size={18} className="text-[rgb(var(--gold-soft))]" />
              {SITE.location}
            </div>
          </div>
        </div>

        {/* Right — native styled form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            noValidate
            className="rounded-2xl border border-[rgba(198,162,99,0.22)] bg-[#0e0c09] p-7 md:p-10"
          >
            {/* Branded header */}
            <div className="mb-8 pb-6 border-b border-[rgba(198,162,99,0.18)]">
              <div className="font-cinzel text-[11px] tracking-[0.32em] uppercase text-[rgb(var(--gold-soft))]">
                Project Enquiry
              </div>
              <div className="mt-2 font-serif-display text-2xl md:text-[26px] text-[rgb(var(--bone))]">
                Brand &amp; Content Brief
              </div>
              <p className="mt-3 text-sm text-[rgb(var(--bone-dim))] leading-relaxed">
                A few quick details about your business, audience, and goals.
                Takes about three minutes.
              </p>
            </div>

            {status === "success" ? (
              <div
                role="status"
                data-testid="contact-success"
                className="flex items-start gap-4 rounded-xl border border-[rgba(198,162,99,0.35)] bg-[rgba(198,162,99,0.06)] px-6 py-8 text-center flex-col items-center"
              >
                <CheckCircle2
                  size={36}
                  className="text-[rgb(var(--gold-soft))]"
                />
                <div className="font-serif-display text-2xl text-[rgb(var(--bone))]">
                  Thank you — your inquiry has been received.
                </div>
                <div className="text-sm text-[rgb(var(--bone-dim))] max-w-md">
                  Uberti Creative will be in touch shortly.
                </div>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Honeypot — visually hidden, off-screen, label hidden from AT */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="cf-website">Website (leave blank)</label>
                    <input
                      id="cf-website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={update("website")}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-name">
                      Name *
                    </label>
                    <input
                      id="cf-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="contact-input-name"
                      className={fieldBase}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-email">
                      Email *
                    </label>
                    <input
                      id="cf-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      data-testid="contact-input-email"
                      className={fieldBase}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="cf-business">
                      Business / Brand Name
                    </label>
                    <input
                      id="cf-business"
                      type="text"
                      value={form.business}
                      onChange={update("business")}
                      data-testid="contact-input-business"
                      className={fieldBase}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-service">
                      Service Interested In
                    </label>
                    <select
                      id="cf-service"
                      value={form.service}
                      onChange={update("service")}
                      data-testid="contact-input-service"
                      className={selectCls}
                      style={selectStyle}
                    >
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-budget">
                      Budget Range
                    </label>
                    <select
                      id="cf-budget"
                      value={form.budget}
                      onChange={update("budget")}
                      data-testid="contact-input-budget"
                      className={selectCls}
                      style={selectStyle}
                    >
                      <option value="">Select a budget</option>
                      {BUDGETS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="cf-timeline">
                      Timeline
                    </label>
                    <select
                      id="cf-timeline"
                      value={form.timeline}
                      onChange={update("timeline")}
                      data-testid="contact-input-timeline"
                      className={selectCls}
                      style={selectStyle}
                    >
                      <option value="">Select a timeline</option>
                      {TIMELINES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="cf-message">
                      Message / Project Details *
                    </label>
                    <textarea
                      id="cf-message"
                      required
                      value={form.message}
                      onChange={update("message")}
                      data-testid="contact-input-message"
                      rows={5}
                      className={`${fieldBase} resize-none`}
                      placeholder="A few sentences about your brand, audience, goals, or what you'd like to improve."
                    />
                  </div>
                </div>

                {status === "error" && errorMsg && (
                  <div
                    role="alert"
                    data-testid="contact-error"
                    className="mt-5 text-sm text-red-300 border border-red-900/50 bg-red-950/30 rounded-lg px-4 py-3"
                  >
                    {errorMsg}
                  </div>
                )}

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-medium btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting"
                      ? "Sending…"
                      : "Submit Inquiry"}
                    <ArrowUpRight size={16} />
                  </button>
                  <span className="text-[11px] tracking-[0.28em] uppercase text-[rgb(var(--bone-dim))]">
                    Replies within 1–2 business days
                  </span>
                </div>
              </>
            )}
          </form>

          {/* Secondary actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-button"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium btn-ghost-gold"
            >
              <Mail size={15} />
              Email Uberti Creative
            </a>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-button"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium btn-ghost-gold"
            >
              <Instagram size={15} />
              Message on Instagram
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
